import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../ui/pagination';
import AssignmentPopup from '../AssignmentPopup';
import Popup from '../Popup';
import { useApplicationReviewsStore } from '../../store/application-reviews';
import { useApplicationsStore } from '../../store/applications';
import { useInsuranceStore } from '../../store/insurance';
import { useDebounce } from '../../lib/hooks';

export enum Tier {
  Tier1 = 'Tier 1',
  Tier1_5 = 'Tier 1.5',
  Tier2 = 'Tier 2',
  Tier2_5 = 'Tier 2.5',
  Tier3 = 'Tier 3',
  Tier3_5 = 'Tier 3.5',
  Tier4 = 'Tier 4',
  Tier4_5 = 'Tier 4.5',
  Tier5 = 'Tier 5',
  Tier5_5 = 'Tier 5.5',
  Tier6 = 'Tier 6',
  Tier7 = 'Tier 7',
}

interface ActionPopupProps {
  isOpen: boolean;
  onClose: () => void;
  memberId: string;
  selectedItem?: any; // выбранный элемент с application и payload
  allReviews?: any[]; // список всех review элементов есть доступ к application.payload.plans
  onSubmit: (
    action: 'accept' | 'reject',
    conclusion: string,
    note: string,
    tier?: string
  ) => Promise<void>;
}

const ActionPopup = ({
  isOpen,
  onClose,
  onSubmit,
}: ActionPopupProps) => {
  const [activeTab, setActiveTab] = useState<'accept' | 'reject'>('accept');
  const [note, setNote] = useState('');
  const [tier, setTier] = useState<string>('');
  const [loading, setLoading] = useState(false);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!note.trim()) return;

    try {
      setLoading(true);
      await onSubmit(activeTab, '', note, tier);
      setNote('');
      setTier('');
      setActiveTab('accept');
      onClose();
    } catch (error) {
      console.error('Error submitting action:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNote('');
    setTier('');
    setActiveTab('accept');
    onClose();
  };

  const availableTiers = Object.values(Tier);

  return (
    <Popup isOpen={isOpen} onClose={handleClose}>
      <h2 className="text-lg font-semibold">Assignment</h2>

      <p className="text-sm text-gray-600 mb-4">
        You are about to evaluate PHQ for the member ID.
      </p>

      <div className="flex mb-4 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setActiveTab('accept')}
          className={`px-4 py-2 text-sm font-medium border-b transition-colors ${
            activeTab === 'accept'
              ? 'border-green-500 text-green-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Approve
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('reject')}
          className={`px-4 py-2 text-sm font-medium border-b transition-colors ${
            activeTab === 'reject'
              ? 'border-red-500 text-red-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Reject
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {activeTab === 'accept' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tier *
            </label>
            <select
              value={tier}
              onChange={e => setTier(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select tier...</option>
              {availableTiers.map((tier: Tier) => (
                <option key={tier} value={tier}>
                  {tier || 'No tier'}
                </option>
              ))}
            </select>
            {availableTiers.length === 0 && (
              <p className="text-sm text-gray-500 mt-1">No tiers available</p>
            )}
          </div>
        )}


        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Note *
          </label>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter note"
            rows={2}
            required
          />
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 px-4 py-2 bg-light-gray rounded-full hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className={`flex-1 px-4 py-2 bg-secondary-bg text-white rounded-full transition-colors ${
              loading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {loading
              ? 'Processing...'
              : activeTab === 'accept'
              ? 'Approve'
              : 'Reject'}
          </button>
        </div>
      </form>
    </Popup>
  );
};

const HumanReview = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'pending' | 'archive'>('pending');
  const [currentPage, setCurrentPage] = useState(1);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [contextMenu, setContextMenu] = useState<{
    isOpen: boolean;
    memberId: string;
    x: number;
    y: number;
  }>({
    isOpen: false,
    memberId: '',
    x: 0,
    y: 0,
  });
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [isPdfOpen, setIsPdfOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [loadingShowPHQ, setLoadingShowPHQ] = useState<string | null>(null);
  const [loadingDownloadMember, setLoadingDownloadMember] = useState<
    string | null
  >(null);
  const [loadingDownloadApp, setLoadingDownloadApp] = useState<string | null>(
    null
  );
  const [showActionPopup, setShowActionPopup] = useState<{
    isOpen: boolean;
    memberId: string;
    rowData?: any;
  }>({
    isOpen: false,
    memberId: '',
  });
  const itemsPerPage = 7;

  const {
    items: applicationReviews,
    loading,
    loadingDetails,
    error,
    loadList,
    setStatus,
    setSearch,
    setPage,
    search,
    status: currentStatus,
    setSort,
    column,
    order,
    // loadOne,
    reject,
    accept,
    current,
  } = useApplicationReviewsStore();

  const { plans: allPlans } = useInsuranceStore();
  const { getPayloadPdfUrl } = useApplicationsStore();

  const getPlansTierByPriceId = (idPlan: number, priceId: number) => {
    const prices = allPlans.find(e => e.id === idPlan)?.prices;
    const tier = prices?.find(e => e.id === priceId)?.tier;
    return tier;
  };

  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    const status = activeTab === 'pending' ? 0 : null;
    setStatus(status);
  }, [activeTab, setStatus]);

  useEffect(() => {
    loadList();
  }, [loadList, debouncedSearch, currentStatus]);

  const transformApplicationReview = (appReview: any) => {
    const user = appReview.user || {};
    const application = appReview.application || {};
    const payload = application.payload || {};
    const plans = payload.plans || [];
    const firstPlan = plans[0] || {};
    
    return {
      memberId: String(appReview.user.id || ''),
      id: String(appReview.id || ''),
      firstName: String(user.firstName || ''),
      lastName: String(user.lastName || ''),
      dateCompleted: appReview.createdAt
        ? new Date(appReview.createdAt).toLocaleDateString()
        : '',
      completed: appReview.updatedAt
        ? new Date(appReview.updatedAt).toLocaleDateString()
        : '',
      underwritten: appReview.status ? 'Completed' : 'Pending',
      by: String(appReview.reviewedBy || ''),
      status: appReview.status,
      tier: firstPlan.id && appReview.priceId 
        ? getPlansTierByPriceId(firstPlan.id, appReview.priceId)
        : '',
      assignment:
        appReview.status === -1
          ? 'Rejected'
          : appReview.status === 1
          ? 'Approved'
          : 'Pending',
      reason: appReview.reason,
      application: appReview.application,
    };
  };
  const processedData =
    applicationReviews.length > 0
      ? applicationReviews.map(transformApplicationReview)
      : [];

  const filteredData = processedData.filter((item: any) => {
    const searchTerm = search.toLowerCase();
    const firstName = String(item.firstName || '').toLowerCase();
    const lastName = String(item.lastName || '').toLowerCase();
    const memberId = String(item.memberId || '').toLowerCase();

    return (
      firstName.includes(searchTerm) ||
      lastName.includes(searchTerm) ||
      memberId.includes(searchTerm) ||
      item.id.includes(searchTerm)
    );
  });

  console.log('filteredData', filteredData);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setPage(page); // Синхронизируем с store
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    let newColumn: 'id' | 'firstName' | 'lastName' | 'createdAt' | 'updatedAt' =
      'createdAt';
    let newOrder: 'asc' | 'desc' = 'desc';

    switch (value) {
      case 'createdAt-desc':
        newColumn = 'createdAt';
        newOrder = 'desc';
        break;
      case 'createdAt-asc':
        newColumn = 'createdAt';
        newOrder = 'asc';
        break;
      case 'firstName-asc':
        newColumn = 'firstName';
        newOrder = 'asc';
        break;
      case 'firstName-desc':
        newColumn = 'firstName';
        newOrder = 'desc';
        break;
      default:
        newColumn = 'createdAt';
        newOrder = 'desc';
    }

    setSort(newColumn, newOrder);
    loadList();
  };

  // const handleReviewDetail = async (memberId: string) => {
  //   // Если уже загружается детали для этого элемента, пропускаем
  //   if (loadingDetails && expandedId === memberId) return;

  //   try {
  //     await loadOne(memberId);
  //     setExpandedId(memberId);
  //   } catch (error) {
  //     console.error('Failed to load review details:', error);
  //     setExpandedId(null); // Скрываем область деталей при ошибке
  //   }
  // };

  const handleRejectAccept = (memberId: string, rowData?: any) => {
    setShowActionPopup({
      isOpen: true,
      memberId,
      rowData,
    });
  };

  const handleCloseActionPopup = () => {
    setShowActionPopup({
      isOpen: false,
      memberId: '',
      rowData: undefined,
    });
  };

  const AssignmentBadge = ({
    assignment,
    status,
  }: {
    assignment: string;
    status: number;
  }) => {
    const isTier = status === 1;
    return (
      <span
        className={`px-2 sm:px-3 py-1 rounded-full text-xs text-white whitespace-nowrap ${
          isTier ? 'status-done' : 'status-pending'
        }`}
      >
        {isTier ? assignment : 'DTQ'}
      </span>
    );
  };
  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setSelectedMemberId('');
  };

  const handleContextMenuClick = (e: React.MouseEvent, memberId: string) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setContextMenu({
      isOpen: true,
      memberId,
      x: rect.right - 150,
      y: rect.bottom + 5,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu({
      isOpen: false,
      memberId: '',
      x: 0,
      y: 0,
    });
  };

  const handleTabChange = (tab: 'pending' | 'archive') => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handleShowPHQ = async (memberId: string) => {
    try {
      setLoadingShowPHQ(memberId);
      const item = activeTabCurrentData.find(e => e.memberId === memberId);
      const applicationId = item?.application?.id;
      if (!applicationId) return;
      const url = await getPayloadPdfUrl(applicationId);
      if (url) {
        setPdfUrl(url);
        setIsPdfOpen(true);
      }
    } catch (err) {
      // noop
    } finally {
      handleCloseContextMenu();
      setLoadingShowPHQ(null);
    }
  };

  const triggerDownload = (url: string, filename: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke later to ensure download starts
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  const handleDownloadPHQ = async (memberId: string) => {
    try {
      setLoadingDownloadMember(memberId);
      const item = activeTabCurrentData.find(e => e.memberId === memberId);
      const applicationId = item?.application?.id;
      if (!applicationId) return;
      const url = await getPayloadPdfUrl(applicationId);
      triggerDownload(url, `phq-${applicationId}.pdf`);
    } catch (err) {
      // noop
    } finally {
      handleCloseContextMenu();
      setLoadingDownloadMember(null);
    }
  };

  const handleReview = (memberId: string) => {
    const item = activeTabCurrentData.find(e => e.memberId === memberId);
    if (item) {
      navigate(`/underwriting/${item.id}`);
    }
    handleCloseContextMenu();
  };

  const handleDownloadPHQById = async (applicationId: string) => {
    try {
      setLoadingDownloadApp(applicationId);
      const url = await getPayloadPdfUrl(applicationId);
      triggerDownload(url, `phq-${applicationId}.pdf`);
    } catch (err) {
      // noop
    } finally {
      setLoadingDownloadApp(null);
    }
  };

  const activeTabCurrentData = currentData.filter(e => {
    // return e;
    if (activeTab === 'archive') {
      return e.status != 0;
    } else {
      return e.status === 0;
    }
  });

  return (
    <div className="flex-1 h-full rounded-[32px] p-2 id-card-container overflow-y-auto max-lg:my-2.5">
      <div className="p-2 pt-8 h-full flex flex-col max-container">
        <div className="flex justify-between items-center mb-6 max-md:flex-col max-md:items-baseline max-md:gap-5">
          <h1 className="id-card-title font-medium ml-6 max-lg:!text-2xl max-md:!text-xl max-md:ml-0">
            PHQs Underwriting
          </h1>

          <div
            className="w-[30%] card-corner p-1 max-md:w-full"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
          >
            <div className="flex gap-2">
              <button
                onClick={() => handleTabChange('pending')}
                className={`px-4 py-2 rounded-lg w-1/2 font-medium max-lg:text-sm transition-colors ${
                  activeTab === 'pending'
                    ? 'bg-white text-black'
                    : 'bg-transparent text-[#71717A] hover:text-[#71717A]'
                }`}
              >
                Pending
              </button>
              <button
                onClick={() => handleTabChange('archive')}
                className={`px-4 py-2 rounded-lg w-1/2 font-medium max-lg:text-sm transition-colors ${
                  activeTab === 'archive'
                    ? 'bg-white text-black'
                    : 'bg-transparent text-[#71717A] hover:text-[#71717A]'
                }`}
              >
                Archive
              </button>
            </div>
          </div>
        </div>
        <div className="space-y-6 bg-white-overlay big-corner p-6 h-full max-md:p-0 max-md:bg-transparent flex flex-col justify-between">
          <div>
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-6 max-md:mb-0 gap-4">
              <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-[40%] xl:w-[60%]">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    placeholder="Search by name or member ID..."
                    value={search}
                    onChange={handleSearchChange}
                    className="w-full px-4 py-3 pl-8 h-12 sm:h-10 border rounded-lg focus:outline-none focus:ring-2 id-card-input text-sm sm:text-base"
                  />
                  <img
                    src="/icons/Search.svg"
                    alt="search"
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5"
                  />
                </div>
                <button
                  onClick={() => {
                    setSearch('');
                    loadList();
                  }}
                  className="px-6 py-3 sm:py-2 max-sm:hidden rounded-full bg-secondary-bg text-white hover:bg-opacity-90 transition-colors text-sm sm:text-base whitespace-nowrap"
                >
                  Search
                </button>
              </div>
              <div className="flex flex-row items-center gap-2 w-full lg:w-auto">
                <span className="text-sm font-medium">Sort</span>
                <select
                  value={`${column}-${order}`}
                  onChange={handleSortChange}
                  className="px-4 py-3 sm:py-2 border rounded-lg w-full md:w-48 focus:outline-none focus:ring-2 id-card-input text-sm sm:text-base"
                >
                  <option value="createdAt-desc">Newest to Oldest</option>
                  <option value="createdAt-asc">Oldest to Newest</option>
                  <option value="firstName-asc">Name A-Z</option>
                  <option value="firstName-desc">Name Z-A</option>
                </select>
              </div>
            </div>

            <div className="overflow-auto">
              {loading && (
                <div className="flex justify-center items-center py-8">
                  <div className="text-sm text-gray-600">
                    Loading application reviews...
                  </div>
                </div>
              )}
              {error && (
                <div className="flex justify-center items-center py-8">
                  <div className="text-sm text-red-600">
                    Error loading data: {error}
                  </div>
                </div>
              )}
              {!loading && !error && (
                <Table className="hidden xl:table xl:overflow-x-auto max-xl:overflow-x-scroll max-xl:w-[150%] max-sm:w-[200%]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px] min-w-[50px] text-xs sm:text-sm">
                        ID
                      </TableHead>
                      <TableHead className="w-[150px] min-w-[120px] text-xs sm:text-sm">
                        MemberID
                      </TableHead>
                      <TableHead className="min-w-[100px] text-xs sm:text-sm">
                        First Name
                      </TableHead>
                      <TableHead className="min-w-[100px] text-xs sm:text-sm">
                        Last Name
                      </TableHead>
                      {activeTab === 'pending' ? (
                        <>
                          <TableHead className="min-w-[120px] text-xs sm:text-sm">
                            Date Completed
                          </TableHead>
                          <TableHead className="text-right min-w-[140px] text-xs sm:text-sm">
                            Actions
                          </TableHead>
                        </>
                      ) : (
                        <>
                          <TableHead className="min-w-[100px] text-xs sm:text-sm">
                            Completed
                          </TableHead>
                          <TableHead className="min-w-[100px] text-xs sm:text-sm">
                            Underwritten
                          </TableHead>
                          <TableHead className="min-w-[80px] text-xs sm:text-sm">
                            By
                          </TableHead>
                          <TableHead className="min-w-[80px] text-xs sm:text-sm">
                            Reason
                          </TableHead>
                          <TableHead className="min-w-[100px] text-xs sm:text-sm">
                            Assignment
                          </TableHead>
                          <TableHead className="text-right min-w-[60px] text-xs sm:text-sm"></TableHead>
                        </>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeTabCurrentData.map((row: any, index: number) => (
                      <>
                        <TableRow key={index}>
                          <TableCell className="font-medium text-xs sm:text-sm">
                            {row.id}
                          </TableCell>
                          <TableCell className="font-medium text-xs sm:text-sm">
                            {row.memberId}
                          </TableCell>
                          <TableCell className="text-xs sm:text-sm">
                            {row.firstName}
                          </TableCell>
                          <TableCell className="text-xs sm:text-sm">
                            {row.lastName}
                          </TableCell>
                          {activeTab === 'pending' ? (
                            <TableCell className="text-xs sm:text-sm">
                              {(row as any).dateCompleted}
                            </TableCell>
                          ) : (
                            <>
                              <TableCell className="text-xs sm:text-sm">
                                {(row as any).completed}
                              </TableCell>
                              <TableCell className="text-xs sm:text-sm">
                                {(row as any).underwritten}
                              </TableCell>
                              <TableCell className="text-xs sm:text-sm">
                                {(row as any).by}
                              </TableCell>
                              <TableCell className="text-xs sm:text-sm">
                                {(row as any).reason}
                              </TableCell>
                              <TableCell className="text-xs sm:text-sm">
                                <AssignmentBadge
                                  assignment={(row as any).tier}
                                  status={(row as any).status}
                                />
                              </TableCell>
                            </>
                          )}
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2 sm:gap-1">
                              <button
                                onClick={e =>
                                  handleContextMenuClick(e, row.memberId)
                                }
                                className="p-3 sm:p-2 border-2 border-gray-200 hover:bg-gray-100 rounded-full w-[44px] h-[44px] sm:w-[36px] sm:h-[36px] flex items-center justify-center"
                              >
                                <img
                                  className="min-w-[10px]"
                                  src="/icons/dots.svg"
                                  alt="dots"
                                />
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                        {expandedId === row.memberId && (
                          <TableRow>
                            <TableCell
                              colSpan={activeTab === 'pending' ? 5 : 8}
                            >
                              <div className="bg-gray-50 p-4 rounded-lg mt-2">
                                <h4 className="font-medium text-lg mb-3">
                                  Review Details
                                </h4>
                                {loadingDetails ? (
                                  <div className="flex justify-center items-center py-8">
                                    <div className="text-sm text-gray-600">
                                      Loading review details...
                                    </div>
                                  </div>
                                ) : current ? (
                                  <>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div>
                                        <strong>Application ID:</strong>{' '}
                                        {current.id}
                                      </div>
                                      <div>
                                        <strong>Status:</strong>{' '}
                                        {current.status}
                                      </div>
                                      <div>
                                        <strong>Reviewed By:</strong>{' '}
                                        {current.reviewedBy}
                                      </div>
                                      <div>
                                        <strong>Created:</strong>{' '}
                                        {new Date(
                                          current.createdAt
                                        ).toLocaleDateString()}
                                      </div>
                                      <div>
                                        <strong>Updated:</strong>{' '}
                                        {new Date(
                                          current.updatedAt
                                        ).toLocaleDateString()}
                                      </div>
                                      {current.user && (
                                        <div>
                                          <strong>User:</strong>{' '}
                                          {current.user.firstName}{' '}
                                          {current.user.lastName}
                                        </div>
                                      )}
                                      {current.application && (
                                        <div>
                                          <strong>Application Status:</strong>{' '}
                                          {current.application.status}
                                        </div>
                                      )}
                                    </div>
                                    <div className="mt-4">
                                      <strong>Conclusия:</strong>
                                      <p className="mt-1 p-2 bg-white rounded border">
                                        {current.conclusion || 'N/A'}
                                      </p>
                                    </div>
                                    <div className="mt-3">
                                      <strong>Notes:</strong>
                                      <p className="mt-1 p-2 bg-white rounded border">
                                        {current.note || 'No notes'}
                                      </p>
                                    </div>
                                  </>
                                ) : (
                                  <div className="flex justify-center items-center py-8">
                                    <div className="text-sm text-gray-600">
                                      Failed to load details
                                    </div>
                                  </div>
                                )}
                                <button
                                  onClick={() => setExpandedId(null)}
                                  className="mt-4 px-4 py-2 bg-gray-600 text-white rounded"
                                >
                                  Close Details
                                </button>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    ))}
                  </TableBody>
                </Table>
              )}

              <div className="xl:hidden space-y-4 mt-6">
                {loading && (
                  <div className="flex justify-center items-center py-8">
                    <div className="text-sm text-gray-600">
                      Loading application reviews...
                    </div>
                  </div>
                )}
                {error && (
                  <div className="flex justify-center items-center py-8">
                    <div className="text-sm text-red-600">
                      Error loading data: {error}
                    </div>
                  </div>
                )}
                {!loading &&
                  !error &&
                  activeTabCurrentData.map((row: any, index: number) => (
                    <div
                      key={index}
                      className="bg-white rounded-[20px] border border-gray-200 p-4 shadow-sm"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-semibold text-lg">
                            {row.firstName} {row.lastName}
                          </h3>
                          <p className="text-sm text-gray-600">
                            ID: {row.memberId}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          {activeTab === 'pending' ? (
                            <>
                              <button
                                onClick={() =>
                                  handleDownloadPHQById(row.application.id)
                                }
                                disabled={
                                  loadingDownloadApp === row.application.id
                                }
                                className={`p-2 border border-gray-300 rounded-full w-8 h-8 flex items-center justify-center ${
                                  loadingDownloadApp === row.application.id
                                    ? 'opacity-50 cursor-not-allowed'
                                    : 'hover:bg-gray-100'
                                }`}
                                title={
                                  loadingDownloadApp === row.application.id
                                    ? 'Downloading…'
                                    : 'Download PHQ'
                                }
                              >
                                {loadingDownloadApp === row.application.id ? (
                                  <svg
                                    className="animate-spin h-4 w-4 text-gray-500"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                      fill="none"
                                    />
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                    />
                                  </svg>
                                ) : (
                                  <img
                                    src="/icons/arrow-up.svg"
                                    alt="download"
                                    className="w-4 h-4"
                                  />
                                )}
                              </button>
                              <button
                                onClick={() =>
                                  handleRejectAccept(row.memberId, row)
                                }
                                className="p-2 text-white bg-secondary-bg rounded-full w-8 h-8 flex items-center justify-center"
                              >
                                <img
                                  src="/icons/justice.svg"
                                  alt="underwriting"
                                  className="w-4 h-4"
                                />
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={e =>
                                handleContextMenuClick(e, row.memberId)
                              }
                              className="p-2 border rotate-90 border-gray-300 hover:bg-gray-100 rounded-full w-8 h-8 flex items-center justify-center"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2 text-sm">
                        {activeTab === 'pending' ? (
                          <>
                            <div className="flex justify-between">
                              <span className="text-gray-600">
                                Date Completed:
                              </span>
                              <span>{row.dateCompleted}</span>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Completed:</span>
                              <span>{row.completed}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">
                                Underwritten:
                              </span>
                              <span>{row.underwritten}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">By:</span>
                              <span>{row.by}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Reason:</span>
                              <span>{row.reason}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Assignment:</span>
                              <AssignmentBadge
                                assignment={row.tier}
                                status={row.status}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          <div className="flex justify-center mt-auto">
            <Pagination>
              <PaginationContent className="flex-wrap gap-1">
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() =>
                      handlePageChange(Math.max(1, currentPage - 1))
                    }
                    className={`cursor-pointer text-sm sm:text-base px-2 sm:px-4 py-2 ${
                      currentPage === 1
                        ? 'pointer-events-none opacity-50'
                        : 'cursor-pointer'
                    }`}
                  />
                </PaginationItem>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  page => (
                    <PaginationItem key={page}>
                      <PaginationLink
                        onClick={() => handlePageChange(page)}
                        isActive={currentPage === page}
                        className="cursor-pointer text-sm sm:text-base px-2 sm:px-3 py-2 min-w-[32px] sm:min-w-[40px]"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  )
                )}

                <PaginationItem>
                  <PaginationNext
                    onClick={() =>
                      handlePageChange(Math.min(totalPages, currentPage + 1))
                    }
                    className={`cursor-pointer text-sm sm:text-base px-2 sm:px-4 py-2 ${
                      currentPage === totalPages
                        ? 'pointer-events-none opacity-50'
                        : 'cursor-pointer'
                    }`}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </div>
      </div>

      <AssignmentPopup
        isOpen={isPopupOpen}
        onClose={handleClosePopup}
        memberId={selectedMemberId}
      />

      {showActionPopup.isOpen && (
        <ActionPopup
          isOpen={showActionPopup.isOpen}
          onClose={handleCloseActionPopup}
          memberId={showActionPopup.memberId}
          selectedItem={showActionPopup.rowData}
          allReviews={applicationReviews}
          onSubmit={async (action, note, tier?) => {
            try {
              if (action === 'accept') {
                await accept(showActionPopup.memberId, {
                  tier: tier || '',
                  // conclusion,
                  note,
                });
              } else {
                await reject(showActionPopup.memberId, { note });
              }
              handleCloseActionPopup();
              loadList(); // refresh the list
            } catch (error) {
              console.error(`Failed to ${action} review:`, error);
            }
          }}
        />
      )}

      {contextMenu.isOpen && (
        <div
          className="fixed bg-white card-corner shadow-lg border border-gray-200 z-50 w-fit"
          style={{
            left: contextMenu.x,
            top: contextMenu.y,
          }}
        >
          {activeTab === 'pending' ? (
            <>
              <button
                onClick={() => handleDownloadPHQById(contextMenu.memberId)}
                disabled={loadingDownloadApp === contextMenu.memberId}
                className={`block w-full px-3 py-2 text-left text-sm transition-colors whitespace-nowrap ${
                  loadingDownloadApp === contextMenu.memberId
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {loadingDownloadApp === contextMenu.memberId ? 'Downloading…' : 'Download PDF'}
              </button>
              <button
                onClick={() => handleReview(contextMenu.memberId)}
                className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                Review
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => handleShowPHQ(contextMenu.memberId)}
                disabled={loadingShowPHQ === contextMenu.memberId}
                className={`block w-full px-3 py-2 text-left text-sm transition-colors whitespace-nowrap ${
                  loadingShowPHQ === contextMenu.memberId
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {loadingShowPHQ === contextMenu.memberId ? 'Opening…' : 'Show PHQ'}
              </button>
              <button
                onClick={() => handleDownloadPHQ(contextMenu.memberId)}
                disabled={loadingDownloadMember === contextMenu.memberId}
                className={`block w-full px-3 py-2 text-left text-sm transition-colors whitespace-nowrap ${
                  loadingDownloadMember === contextMenu.memberId
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {loadingDownloadMember === contextMenu.memberId
                  ? 'Downloading…'
                  : 'Download PHQ'}
              </button>
            </>
          )}
        </div>
      )}

      {contextMenu.isOpen && (
        <div className="fixed inset-0 z-40" onClick={handleCloseContextMenu} />
      )}

      <Popup isOpen={isPdfOpen} onClose={() => setIsPdfOpen(false)} fullscreen>
        {pdfUrl ? (
          <iframe src={pdfUrl} title="PHQ PDF" className="w-full h-full" />
        ) : null}
      </Popup>
    </div>
  );
};

export default HumanReview;
